/** Overview — every branch at a glance for one day (spec §4.1 screen 1). */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Wifi, WifiOff, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { api, errorText, label, STATUS, today } from './shared';

const ORDER = ['present', 'in_progress', 'needs_review', 'absent_unexcused', 'absent_excused', 'on_leave',
  'paid_day_off', 'half_day_leave', 'half_day_unexcused', 'off', 'holiday', 'future', 'not_employed'];

export default function OverviewTab({ token, isRTL, go }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [day, setDay] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await api(token).get('/overview', { day })); } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, [token, day]);
  useEffect(() => { load(); }, [load]);

  const dev = data?.devices;
  return (
    <div className="space-y-4 mt-4" data-testid="attendance-overview">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" data-testid="overview-day" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        {dev && (
          <div className="flex items-center gap-4 text-sm text-slate-600 ms-auto" data-testid="overview-devices">
            <span className="flex items-center gap-1">
              {dev.online === dev.total ? <Wifi className="h-4 w-4 text-emerald-600" /> : <WifiOff className="h-4 w-4 text-rose-600" />}
              {t(`Devices online ${dev.online} of ${dev.total}`, `الأجهزة المتصلة ${dev.online} من ${dev.total}`)}
            </span>
            {data.identity_index?.due > 0 && (
              <button className="text-amber-700 underline" onClick={() => go('settings')} data-testid="overview-index-due">
                {t(`${data.identity_index.due} ID(s) to re-match`, `${data.identity_index.due} هوية بانتظار المطابقة`)}
              </button>
            )}
            {data.work_patterns?.company_default > 0 && (
              <button className="text-amber-700 underline" onClick={() => go('settings')} data-testid="overview-pattern-default">
                {t(`${data.work_patterns.company_default} office employee(s) on the company weekend (no work pattern on profile)`,
                  `${data.work_patterns.company_default} موظف مكتبي على عطلة الشركة (لا نمط عمل في الملف)`)}
              </button>
            )}
            {data.work_patterns?.production_no_roster > 0 && (
              <button className="text-amber-700 underline" onClick={() => go('settings')} data-testid="overview-no-roster">
                {t(`${data.work_patterns.production_no_roster} production/driver employee(s) with no roster this pay period`,
                  `${data.work_patterns.production_no_roster} موظف إنتاج/سائق بلا جدول مناوبات لهذه الفترة`)}
              </button>
            )}
            {dev.pending_approval > 0 && (
              <button className="text-amber-700 underline" onClick={() => go('settings')}>
                {t(`${dev.pending_approval} device(s) awaiting approval`, `${dev.pending_approval} جهاز بانتظار الاعتماد`)}
              </button>
            )}
          </div>
        )}
      </div>
      {error && <div className="text-sm text-rose-600">{error}</div>}
      {loading && !data && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
      {data && !data.branches.length && (
        <div className="brand-empty-state p-8 text-center text-slate-500">{t('No employees yet.', 'لا يوجد موظفون بعد.')}</div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data?.branches.map((b) => (
          <Card key={b.branch_id} data-testid={`overview-branch-${b.branch_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{(isRTL ? b.name_ar || b.name : b.name || b.name_ar) || t('No branch', 'بدون فرع')}</span>
                <span className="text-sm font-normal text-slate-500">{b.employees} {t('employees', 'موظف')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {ORDER.filter((s) => b.statuses[s]).map((s) => (
                  <span key={s} className={`text-xs border rounded-full px-2 py-0.5 ${STATUS[s]?.cls || ''}`}>
                    {label(STATUS, s, isRTL)}: <b>{b.statuses[s]}</b>
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-slate-600"><Clock3 className="h-4 w-4" />{t('Late', 'متأخر')}: {b.late}</span>
                <Button size="sm" variant={b.open_suggestions ? 'default' : 'outline'} disabled={!b.open_suggestions}
                  onClick={() => go('suggestions', { branch: b.branch_id === 'none' ? '' : b.branch_id })}>
                  {t(`${b.open_suggestions} open`, `${b.open_suggestions} مفتوح`)}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
